import React, { useEffect, useState } from 'react';

import { errorMessage } from './managementPackUtils';
import { STATUS_LABELS } from './StatusPill';

import Alert from 'CommonComponents/Alert';
import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import SegmentedControl from 'CommonComponents/SegmentedControl';

const NOTE_MAX = 1000;

const RAG = ['R', 'A', 'G'];

/**
 * Set one area's, department's or plan's status by hand. A note saying why is
 * required; the server's validation message is shown here when it refuses.
 */
const StatusOverrideModal = ({ modal, subject, onSave }) => {
    const [status, setStatus] = useState('G');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (subject) {
            setStatus(subject.manual && RAG.includes(subject.status) ? subject.status : RAG.includes(subject.ruleStatus) ? subject.ruleStatus : 'G');
            setNote(subject.note || '');
            setError(null);
        }
    }, [subject]);

    const close = () => {
        setError(null);
        modal.hide();
    };

    const save = () => {
        if (!note.trim()) {
            setError($t('Add a note saying why the status is set manually.'));
            return;
        }

        setSaving(true);
        setError(null);

        onSave(subject, { status, note: note.trim() })
            .then(() => modal.hide())
            .catch((e) => {
                const errors = e?.response?.data?.errors;
                const first = errors ? Object.values(errors).flat()[0] : null;

                setError(first || errorMessage(e, $t('The status could not be saved. Please try again.')));
            })
            .finally(() => setSaving(false));
    };

    return (
        <Modal modal={modal}>
            <ModalHeader title={$t('Set status manually')} close={close} dark />
            {subject && (
                <div className="tw-flex tw-flex-col tw-gap-4 tw-text-sm tw-text-charcoal">
                    <p className="tw-m-0">
                        <span className="tw-font-semibold">{subject.label}</span>{' '}
                        <span className="tw-text-charcoal-75">
                            {$t('The rules give {{status}}.', { status: STATUS_LABELS[subject.ruleStatus]?.() ?? subject.ruleStatus })}
                        </span>
                    </p>

                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                        <span className="text-input__label">{$t('Status')}</span>
                        <SegmentedControl
                            variant="dark"
                            value={status}
                            onChange={setStatus}
                            disabled={saving}
                            options={RAG.map((value) => ({ value, label: STATUS_LABELS[value]() }))}
                        />
                    </div>

                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                        <label htmlFor="status-override-note" className="text-input__label">
                            {$t('Note')} *
                        </label>
                        <textarea
                            id="status-override-note"
                            className="text-input tw-min-h-8 tw-border-0"
                            rows={4}
                            value={note}
                            maxLength={NOTE_MAX}
                            disabled={saving}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={$t('Why is this status set manually? The note is listed in Appendix A0.')}
                        />
                    </div>

                    {!!error && <Alert type="error">{error}</Alert>}
                </div>
            )}
            <ModalFooter
                handleSubmit={save}
                onCancel={close}
                isSubmitting={saving}
                submitButtonVariant="primary"
                primaryButtonText={$t('Save')}
                cancelButtonText={$t('Cancel')}
            />
        </Modal>
    );
};

export default StatusOverrideModal;
